import React from 'react'
import { ActionList } from '../../../legacy-design-system/action'
import { PureQueryOptions } from 'apollo-client'
import { Role, hasRole } from '../../roles'
import {
  ModifyVulnerabilityTagsAction,
  ModifyVulnerabilityTagsByFilterAction
} from './tag-action'
import { ExportVulnerabilitiesAction } from './export-action'
import { ExportVulnerabilitiesWithFilterAction } from './export-with-filters-action'
import { SendAction } from './send-action'
import { SendWithFilterAction } from './send-with-filters-action'
import {
  StatusByIDsAction,
  StatusByFiltersAction
} from '../../vulnerability-status'
import { MergeVulnerabilitiesAction } from './merge-action'
import { DeleteWithFiltersAction } from './delete-with-filters-action'
import { DeleteAction } from './delete-action'
import { DateTime } from '../shared'

interface GQLVulnerability {
  id: string
  title: string
  ruleName: string
  tags: string[]
  reportedToBugTrackerTime: string | null
  lastDetected: DateTime
  application: {
    id: string
    licensed: boolean
    role: Role
    isParentApp?: boolean
    parentApplicationId?: string
  }
  status: string
  pendingStatus: null | {
    status: string
  }
}

interface ActionProps {
  leastPermissiveApplicationRoleOfSelected: Role
  mostPermissiveApplicationRole: Role
  organizationID: string
  organizationRole: Role
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  vulnerabilities: GQLVulnerability[]
  isFreemiumOrganization: boolean
  trackJob: (job: {
    id: string
    progress: number
    total: number
  }) => Promise<void>
  validateIfVulnerabilitiesAreFromSameApplication?: boolean
  restrictedEditRole?: boolean
}

export function BulkActionList({
  leastPermissiveApplicationRoleOfSelected,
  mostPermissiveApplicationRole,
  organizationID,
  organizationRole,
  refetchQueries,
  refresh,
  vulnerabilities,
  isFreemiumOrganization,
  trackJob,
  validateIfVulnerabilitiesAreFromSameApplication,
  restrictedEditRole
}: ActionProps) {
  // This never actually appears to work in my testing, the buttons are always hidden with the VIEW only role
  const showDisabled = hasRole(mostPermissiveApplicationRole, 'EDIT')
  return (
    <ActionList large>
      {hasRole(leastPermissiveApplicationRoleOfSelected, 'EDIT') && (
        <StatusByIDsAction
          menuPlacement="top"
          organizationID={organizationID}
          refetchQueries={refetchQueries}
          refresh={refresh}
          vulnerabilities={vulnerabilities}
          applicationRole={leastPermissiveApplicationRoleOfSelected}
        />
      )}
      <ModifyVulnerabilityTagsAction
        vulnerabilities={vulnerabilities}
        applicationRole={leastPermissiveApplicationRoleOfSelected}
        showDisabled={showDisabled}
        organizationID={organizationID}
        refetchQueries={refetchQueries}
        refresh={refresh}
        bulk
      />
      <SendAction
        organizationID={organizationID}
        vulnerabilities={vulnerabilities}
        applicationRole={leastPermissiveApplicationRoleOfSelected}
        showDisabled={showDisabled}
        refetchQueries={refetchQueries}
        refresh={refresh}
        validateIfVulnerabilitiesAreFromSameApplication={
          validateIfVulnerabilitiesAreFromSameApplication
        }
      />
      <ExportVulnerabilitiesAction
        selectedVulnerabilityIDs={vulnerabilities.map((v) => v.id)}
        organizationID={organizationID}
        isFreemiumOrganization={isFreemiumOrganization}
      />
      <MergeVulnerabilitiesAction
        organizationID={organizationID}
        organizationRole={organizationRole}
        refetchQueries={refetchQueries}
        refresh={refresh}
        vulnerabilities={vulnerabilities}
      />
      <DeleteAction
        organizationID={organizationID}
        vulnerabilities={vulnerabilities}
        showDisabled={showDisabled}
        refresh={refresh}
        refetchQueries={refetchQueries}
        trackJob={trackJob}
        restrictedEditRole={restrictedEditRole}
      />
    </ActionList>
  )
}

interface FilteredActionProps {
  organizationID: string
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  selectedCount: number
  vulnerabilities: GQLVulnerability[]
  excludedVulnerabilities: GQLVulnerability[]
  leastPermissiveApplicationRoleOfSelected: Role
  filters: {
    filterText: string
    quickFilter: string
    aggregations: Record<string, string[]>
  }
  additionalFilters?: { [key: string]: any }
  isFreemiumOrganization: boolean
  trackJob: (job: {
    id: string
    progress: number
    total: number
  }) => Promise<void>
  validateIfVulnerabilitiesAreFromSameApplication?: boolean
  isAppsGrid?: boolean
}

export function FilteredActionList({
  organizationID,
  refetchQueries,
  refresh,
  excludedVulnerabilities,
  vulnerabilities,
  filters,
  additionalFilters,
  selectedCount,
  isFreemiumOrganization,
  leastPermissiveApplicationRoleOfSelected,
  trackJob,
  validateIfVulnerabilitiesAreFromSameApplication,
  isAppsGrid = false
}: FilteredActionProps) {
  const showDisabled = !hasRole(
    leastPermissiveApplicationRoleOfSelected,
    'EDIT'
  )
  return (
    <ActionList large>
      {!showDisabled && (
        <StatusByFiltersAction
          menuPlacement="top"
          organizationID={organizationID}
          refetchQueries={refetchQueries}
          refresh={refresh}
          excludedVulnerabilities={excludedVulnerabilities}
          filters={filters}
          additionalFilters={additionalFilters}
          vulnerabilities={vulnerabilities}
          applicationRole={leastPermissiveApplicationRoleOfSelected}
        />
      )}
      <ModifyVulnerabilityTagsByFilterAction
        organizationID={organizationID}
        refetchQueries={refetchQueries}
        showDisabled={showDisabled}
        refresh={refresh}
        excludedVulnerabilities={excludedVulnerabilities}
        vulnerabilities={vulnerabilities}
        filters={filters}
        additionalFilters={additionalFilters}
      />
      <SendWithFilterAction
        organizationID={organizationID}
        excludedVulnerabilities={excludedVulnerabilities.map((v) => v.id)}
        filters={filters}
        additionalFilters={additionalFilters}
        selectedCount={selectedCount}
        vulnerabilities={vulnerabilities}
        refetchQueries={refetchQueries}
        showDisabled={showDisabled}
        refresh={refresh}
        validateIfVulnerabilitiesAreFromSameApplication={
          validateIfVulnerabilitiesAreFromSameApplication
        }
      />
      <ExportVulnerabilitiesWithFilterAction
        organizationID={organizationID}
        filters={filters}
        additionalFilters={additionalFilters}
        excludedVulnerabilities={excludedVulnerabilities.map((v) => v.id)}
        isFreemiumOrganization={isFreemiumOrganization}
        isAppsGrid={isAppsGrid}
      />
      <DeleteWithFiltersAction
        organizationID={organizationID}
        excludedVulnerabilities={excludedVulnerabilities.map((v) => v.id)}
        filters={filters}
        additionalFilters={additionalFilters}
        showDisabled={showDisabled}
        selectedCount={selectedCount}
        refresh={refresh}
        refetchQueries={refetchQueries}
        trackJob={trackJob}
      />
    </ActionList>
  )
}
